import DelegationAddFromMileagesRequest from "../../api/raily/delegation/add-from-mileages/delegation-add-from-mileages.request";
import DelegationAddRequest from "../../api/raily/delegation/add/delegation-add.request";
import delegationApiService from "../../api/raily/delegation/delegation-api.service";
import DelegationDeleteRequest from "../../api/raily/delegation/delete/delegation-delete.request";
import DelegationDownloadRequest from "../../api/raily/delegation/download/delegation-download.request";
import DelegationListRequest from "../../api/raily/delegation/list/delegation-list.request";
import dataLoadErrorFactory from "../../utils/data-load-error/data-load-error.factory";
import fileDownloadService from "../../utils/file-download/file-download.service";
import HttpError from "../../utils/http/http.error";
import DelegationAddFromMileagesError from "./add-from-mileages/delegation-add-from-mileages-error";
import DelegationAddFromMileagesParams from "./add-from-mileages/delegation-add-from-mileages-params";
import delegationAddFromMileagesParamsFactory from "./add-from-mileages/delegation-add-from-mileages-params.factory";
import DelegationAddError from "./add/delegation-add-error";
import DelegationAddParams from "./add/delegation-add-params";
import delegationAddParamsFactory from "./add/delegation-add-params.factory";
import DelegationDeleteError from "./delete/delegation-delete-error";
import DelegationDeleteParams from "./delete/delegation-delete-params";
import DelegationDownloadError from "./download/delegation-download-error";
import DelegationDownloadParams from "./download/delegation-download-params";
import delegationDownloadParamsFactory from "./download/delegation-download-params.factory";
import DelegationDetails from "./details/delegation-details";
import DelegationDetailsError from "./details/delegation-details-error";
import DelegationDetailsParams from "./details/delegation-details-params";
import delegationDetailsParamsFactory from "./details/delegation-details-params.factory";
import delegationDetailsFactory from "./details/delegation-details.factory";
import DelegationListError from "./list/delegation-list-error";
import DelegationListParams from "./list/delegation-list-params";
import delegationListParamsFactory from "./list/delegation-list-params.factory";
import delegationListFactory from "./list/delegation-list.factory";
import DelegationRoadRouteItem from "./road-route/delegation-road-route-item";
import DelegationRoadRouteParams from "./road-route/delegation-road-route-params";
import delegationRoadRouteParamsFactory from "./road-route/delegation-road-route-params.factory";
import delegationRoadRouteFactory from "./road-route/delegation-road-route.factory";
import DelegationList from "./list/delegation-list";
import delegationAddFromAllMileagesRequestFactory from "./add-from-all-mileages/delegation-add-from-all-mileages-request.factory";
import DelegationAddFromAllMileagesRequest from "../../api/raily/delegation/add-from-all-mileages/delegation-add-from-all-mileages.request";
import DelegationAddFromAllMileagesError from "./add-from-all-mileages/delegation-add-from-all-mileages-error";
import DelegationAddFromAllMileagesParams from "./add-from-all-mileages/delegation-add-from-all-mileages-params";
import delegationAddFromAllMileagesErrorFactory from "./add-from-all-mileages/delegation-add-from-all-mileages-error.factory";
import handleErrorFactory from "../../utils/handle-error/handle-error.factory";

const getList = async (
  loadParams: DelegationListParams,
  abortSignal: AbortSignal,
): Promise<DelegationList> => {
  const request: DelegationListRequest =
    delegationListParamsFactory.create(loadParams);

  try {
    const response = await delegationApiService().list(request, abortSignal);

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    const delegationList = delegationListFactory.create(response);
    return delegationList;
  } catch (_error) {
    const error = _error as HttpError | DelegationListError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const add = async (
  params: DelegationAddParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: DelegationAddRequest =
    delegationAddParamsFactory.create(params);

  try {
    const response = await delegationApiService().add(request, abortSignal);

    if (response.status !== 201) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | DelegationAddError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const addFromMileages = async (
  params: DelegationAddFromMileagesParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: DelegationAddFromMileagesRequest =
    delegationAddFromMileagesParamsFactory.create(params);

  try {
    const response = await delegationApiService().addFromMileages(
      request,
      abortSignal,
    );

    if (response.status !== 201) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | DelegationAddFromMileagesError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const addFromAllMileages = async (
  params: DelegationAddFromAllMileagesParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: DelegationAddFromAllMileagesRequest =
    delegationAddFromAllMileagesRequestFactory.create(params);

  try {
    const response = await delegationApiService().addFromAllMileages(
      request,
      abortSignal,
    );

    if (response.status === 406) {
      throw delegationAddFromAllMileagesErrorFactory.createNoMileagesAvailableError();
    }

    if (response.status !== 201) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | DelegationAddFromAllMileagesError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const getDetails = async (
  params: DelegationDetailsParams,
  abortSignal: AbortSignal,
): Promise<DelegationDetails> => {
  const request = delegationDetailsParamsFactory.create(params);

  try {
    const response = await delegationApiService().getDetails(
      request,
      abortSignal,
    );

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    return delegationDetailsFactory.create(response);
  } catch (_error) {
    const error = _error as HttpError | DelegationDetailsError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const deleteDelegation = async (
  params: DelegationDeleteParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: DelegationDeleteRequest = {
    delegationUuid: params.delegationUuid,
  };

  try {
    const response = await delegationApiService().deleteDelegation(
      request,
      abortSignal,
    );

    if (response.status !== 204) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }
  } catch (_error) {
    const error = _error as HttpError | DelegationDeleteError;

    throw handleErrorFactory.handleCustomError(error);
  }
};

const download = async (
  params: DelegationDownloadParams,
  abortSignal: AbortSignal,
): Promise<void> => {
  const request: DelegationDownloadRequest =
    delegationDownloadParamsFactory.create(params);

  try {
    const response = await delegationApiService().download(
      request,
      abortSignal,
    );

    if (response.status !== 200) {
      throw dataLoadErrorFactory.createUnexpectedError();
    }

    fileDownloadService.downloadFromBlob(
      response.data.data,
      response.data.filename,
    );
  } catch (_error) {
    const error = _error as HttpError | DelegationDownloadError;

    throw handleErrorFactory.handleCustomError(error);
  }
};
const getRoute = async (
  params: DelegationRoadRouteParams,
): Promise<DelegationRoadRouteItem> => {
  const request = delegationRoadRouteParamsFactory.create(params);

  try {
    const response = await delegationApiService().getRoute(request);

    return delegationRoadRouteFactory.create(response);
  } catch (_error) {
    const error = _error as HttpError;

    throw handleErrorFactory.handleError(error);
  }
};

const delegationService = {
  getList,
  add,
  addFromMileages,
  getDetails,
  deleteDelegation,
  download,
  getRoute,
  addFromAllMileages,
};

export default delegationService;
